import { I18n } from '@calendly/i18n'
import moment from 'moment-timezone'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import type { Meeting, EventMembership } from 'features/meetings/types'
import { timeNotationSelector, timezoneSelector } from 'selectors/current_user'
import { useCurrentTime } from 'shared/contexts'

import { calculateBufferRange, getBufferData, isMultiHost } from './utils'

interface BufferPopupData {
  isBefore: boolean
  userMembership: Record<string, any>
}

export interface BufferEntry {
  bufferLength: string
  memberships: EventMembership[]
  bufferRange: string
  userMembership: EventMembership | undefined
}

export interface BuffersProps {
  entries: BufferEntry[]
  isMultiHost: boolean
  isCompact: boolean
  showBufferEdit: boolean
  testId: string
  label: string
  /** true = before-meeting buffer, false = after-meeting buffer (must match EditBufferPopup `isBefore`) */
  isBeforeBuffer: boolean
  onEditBuffer: (isBefore: boolean, userMembership: EventMembership) => void
}

const buildBufferEntries = ({
  eventMemberships,
  currentUser,
  eventStartTime,
  eventEndTime,
  timeNotation,
  type,
}: {
  eventMemberships: EventMembership[]
  currentUser: { id?: string | number; uuid?: string }
  eventStartTime: moment.Moment
  eventEndTime: moment.Moment
  timeNotation: string
  type: 'start' | 'end'
}): BufferEntry[] => {
  const isStart = type === 'start'
  const bufferData = getBufferData(eventMemberships, type)

  return Object.keys(bufferData)
    .sort((a, b) => (isStart ? parseInt(b, 10) - parseInt(a, 10) : parseInt(a, 10) - parseInt(b, 10)))
    .map((bufferLength) => {
      const memberships = bufferData[bufferLength]
      return {
        bufferLength,
        memberships,
        bufferRange: calculateBufferRange({
          bufferLength,
          timeNotation,
          eventStartTime,
          eventEndTime,
          isStart,
        }),
        userMembership: memberships.find(
          (membership) => membership.userId === currentUser.id || membership.uuid === currentUser.uuid
        ),
      }
    })
}

/**
 * Custom hook to manage buffer time data and UI state for a meeting card.
 *
 * Calculates buffer time entries for the start and end of a meeting,
 * handles popup state for editing buffer times, and provides props
 * for rendering the buffer time sections and popup.
 *
 * @param event - The meeting whose buffer times are managed
 * @returns Object containing buffer section props and popup props
 */
export const useBuffers = (event: Meeting) => {
  const { startTime, endTime } = event

  const [bufferPopupData, setBufferPopupData] = useState<BufferPopupData | null>(null)
  const { data: currentUser } = useGetUserQuery(undefined)
  const timeNotation = useSelector(timeNotationSelector)
  const timezone = useSelector(timezoneSelector)
  const eventStartTime = moment(startTime).tz(timezone)
  const eventEndTime = moment(endTime).tz(timezone)
  const multiHost = isMultiHost(event)
  const currentTime = useCurrentTime()

  const setPopupData = (isBefore: boolean, userMembership: Record<string, any>) => {
    setBufferPopupData({ isBefore, userMembership })
  }

  const startEntries = useMemo(
    () => buildBufferEntries({
      eventMemberships: event.eventMemberships,
      currentUser,
      eventStartTime: moment(startTime).tz(timezone),
      eventEndTime: moment(endTime).tz(timezone),
      timeNotation,
      type: 'start',
    }),
    [event.eventMemberships, currentUser, startTime, endTime, timezone, timeNotation]
  )

  const endEntries = useMemo(
    () => buildBufferEntries({
      eventMemberships: event.eventMemberships,
      currentUser,
      eventStartTime: moment(startTime).tz(timezone),
      eventEndTime: moment(endTime).tz(timezone),
      timeNotation,
      type: 'end',
    }),
    [event.eventMemberships, currentUser, startTime, endTime, timezone, timeNotation]
  )

  return {
    startBufferProps: startEntries.length > 0 ? {
      entries: startEntries,
      isMultiHost: multiHost,
      showBufferEdit: eventStartTime.isAfter(currentTime),
      testId: 'buffer-before',
      label: I18n.t('publisher.scheduled_events.buffers.meeting_buffer.before'),
      isBeforeBuffer: true,
      onEditBuffer: setPopupData,
    } : null,
    endBufferProps: endEntries.length > 0 ? {
      entries: endEntries,
      isMultiHost: multiHost,
      showBufferEdit: eventEndTime.isAfter(currentTime),
      testId: 'buffer-after',
      label: I18n.t('publisher.scheduled_events.buffers.meeting_buffer.after'),
      isBeforeBuffer: false,
      onEditBuffer: setPopupData,
    } : null,
    popupProps: {
      eventId: event.uuid || event.id,
      isMultiHost: multiHost,
      eventName: event.name,
      isBefore: bufferPopupData?.isBefore,
      userMembership: bufferPopupData?.userMembership,
      isOpen: bufferPopupData !== null,
      onClose: () => setBufferPopupData(null),
    },
  }
}
